import { Key } from 'react';
import { Flex, TextField } from '@backstage/ui';
import { ModernSelect } from '@internal/backstage-plugin-devops-react';
import {
  Schedule,
  SchedulePeriod,
} from '@internal/backstage-plugin-catalog-devops-common';

interface AccountScanScheduleInputProps {
  accountScanSchedule: Schedule | undefined;
  setAccountScanSchedule: (schedule: Schedule | undefined) => void;

  isInvalid?: boolean;
  disabled?: boolean;
  hidden?: boolean;
}

const getPeriodLabel = (period: SchedulePeriod) => {
  switch (period) {
    case SchedulePeriod.HOUR:
      return 'Hourly';
    case SchedulePeriod.DAY:
      return 'Daily';
    case SchedulePeriod.WEEK:
      return 'Weekly';
    case SchedulePeriod.MONTH:
      return 'Monthly';
    default:
      return period;
  }
};

export const AccountScanScheduleInput = (
  props: AccountScanScheduleInputProps,
) => {
  const {
    accountScanSchedule,
    setAccountScanSchedule,
    isInvalid,
    disabled,
    hidden,
  } = props;

  // Hide component if hidden prop is true
  if (hidden) {
    return null;
  }

  const handlePeriodChange = (value: Key | Key[] | null) => {
    const selectedLabel = value?.toString() ?? '';
    const period = SchedulePeriod[selectedLabel as keyof typeof SchedulePeriod];
    setAccountScanSchedule({
      period,
      hour: undefined,
      dayOfWeek: undefined,
      dayOfMonth: undefined,
    });
  };

  // `parseInt(...) || undefined` drops the legitimate value 0 (midnight hour,
  // Sunday); only NaN means "no value". Out-of-range input is clamped so the
  // backend never receives garbage.
  const parseIntOrUndefined = (
    value: string,
    min: number,
    max: number,
  ): number | undefined => {
    const parsed = Number.parseInt(value, 10);
    if (Number.isNaN(parsed)) return undefined;
    return Math.min(Math.max(parsed, min), max);
  };

  const handleHourChange = (value: string) => {
    const hour = parseIntOrUndefined(value, 0, 23);
    setAccountScanSchedule({
      period: accountScanSchedule?.period || SchedulePeriod.HOUR,
      hour,
      dayOfWeek: accountScanSchedule?.dayOfWeek,
      dayOfMonth: accountScanSchedule?.dayOfMonth,
    });
  };

  const handleDayOfWeekChange = (value: string) => {
    const dayOfWeek = parseIntOrUndefined(value, 0, 6);
    setAccountScanSchedule({
      period: accountScanSchedule?.period || SchedulePeriod.WEEK,
      hour: accountScanSchedule?.hour,
      dayOfWeek,
      dayOfMonth: accountScanSchedule?.dayOfMonth,
    });
  };

  const handleDayOfMonthChange = (value: string) => {
    const dayOfMonth = parseIntOrUndefined(value, 1, 31);
    setAccountScanSchedule({
      period: accountScanSchedule?.period || SchedulePeriod.MONTH,
      hour: accountScanSchedule?.hour,
      dayOfWeek: accountScanSchedule?.dayOfWeek,
      dayOfMonth,
    });
  };

  const getMenuItems = () => {
    return Object.values(SchedulePeriod).map(period => ({
      label: getPeriodLabel(period),
      value: period,
    }));
  };

  const renderScheduleFields = () => {
    if (!accountScanSchedule?.period) return null;

    switch (accountScanSchedule.period) {
      case SchedulePeriod.HOUR:
        return null;
      case SchedulePeriod.DAY:
        return (
          <TextField
            label="Hour (0-23)"
            value={accountScanSchedule.hour?.toString() ?? ''}
            onChange={value => handleHourChange(value)}
            size="small"
            isDisabled={disabled}
          />
        );
      case SchedulePeriod.WEEK:
        return (
          <Flex gap="1" direction="column">
            <TextField
              label="Day of Week (0-6)"
              value={accountScanSchedule.dayOfWeek?.toString() ?? ''}
              onChange={handleDayOfWeekChange}
              size="small"
              isDisabled={disabled}
              description="0=Sunday, 1=Monday, etc."
            />
            <TextField
              label="Hour (0-23)"
              value={accountScanSchedule.hour?.toString() ?? ''}
              onChange={value => handleHourChange(value)}
              size="small"
              isDisabled={disabled}
            />
          </Flex>
        );
      case SchedulePeriod.MONTH:
        return (
          <Flex gap="1" direction="column">
            <TextField
              label="Day of Month (1-31)"
              value={accountScanSchedule.dayOfMonth?.toString() ?? ''}
              onChange={handleDayOfMonthChange}
              size="small"
              isDisabled={disabled}
            />
            <TextField
              label="Hour (0-23)"
              value={accountScanSchedule.hour?.toString() ?? ''}
              onChange={handleHourChange}
              size="small"
              isDisabled={disabled}
            />
          </Flex>
        );
      default:
        return null;
    }
  };

  return (
    <Flex direction="column" gap="1">
      <ModernSelect
        label="Scan Schedule"
        options={getMenuItems()}
        value={accountScanSchedule?.period ?? ''}
        onChange={value => handlePeriodChange(value)}
        isInvalid={isInvalid}
        isDisabled={disabled}
        isRequired
        emptyOptionLabel="Without scan schedule"
      />
      {renderScheduleFields()}
    </Flex>
  );
};
